import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import PrivateRoute from './components/PrivateRoute';
import PermissionRoute from './components/PermissionRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TicketList from './pages/TicketList';
import CreateTicket from './pages/CreateTicket';
import TicketDetails from './pages/TicketDetails';
import CompanyManagement from './pages/CompanyManagement';
import UserManagement from './pages/UserManagement';
import TimeTracking from './pages/TimeTracking';
import TeamUsage from './pages/TeamUsage';
import ProfileSettings from './pages/ProfileSettings';
import ActivateAccount from './pages/ActivateAccount';
import AuditLog from './pages/AuditLog';
import InvoiceList from './pages/InvoiceList';
import CreateInvoice from './pages/CreateInvoice';
import InvoiceView from './pages/InvoiceView';
import Deals from './pages/Deals';
import DealPdf from './pages/DealPdf';
import ProjectsHome from './pages/projects/ProjectsHome';
import ProjectBoard from './pages/projects/ProjectBoard';
import { useAuth } from './context/AuthContext';
import { UserRole } from './types';
import { Permission } from './utils/permissions';

// PrimeReact CSS
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === UserRole.END_USER) {
    return <Navigate to="/tickets" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/activate-account" element={<ActivateAccount />} />
            <Route path="/invoice/:id" element={<InvoiceView />} />
            <Route
              path="/deal/:id"
              element={
                <PrivateRoute>
                  <DealPdf />
                </PrivateRoute>
              }
            />

            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<HomeRedirect />} />
              <Route
                path="dashboard"
                element={
                  <PermissionRoute permission={Permission.VIEW_DASHBOARD}>
                    <Dashboard />
                  </PermissionRoute>
                }
              />
              <Route
                path="tickets"
                element={
                  <PermissionRoute permission={Permission.VIEW_OWN_TICKETS}>
                    <TicketList />
                  </PermissionRoute>
                }
              />
              <Route
                path="tickets/new"
                element={
                  <PermissionRoute permission={Permission.CREATE_TICKET}>
                    <CreateTicket />
                  </PermissionRoute>
                }
              />
              <Route
                path="tickets/:id"
                element={
                  <PermissionRoute permission={Permission.VIEW_OWN_TICKETS}>
                    <TicketDetails />
                  </PermissionRoute>
                }
              />
              <Route
                path="companies"
                element={
                  <PermissionRoute permission={Permission.VIEW_COMPANIES}>
                    <CompanyManagement />
                  </PermissionRoute>
                }
              />
              <Route
                path="users"
                element={
                  <PermissionRoute permission={Permission.VIEW_USERS}>
                    <UserManagement />
                  </PermissionRoute>
                }
              />
              <Route
                path="audit-logs"
                element={
                  <PermissionRoute permission={Permission.VIEW_AUDIT_LOGS}>
                    <AuditLog />
                  </PermissionRoute>
                }
              />
              <Route
                path="time-tracking"
                element={
                  <PermissionRoute permission={Permission.VIEW_TIME_TRACKING}>
                    <TimeTracking />
                  </PermissionRoute>
                }
              />
              <Route
                path="invoices"
                element={
                  <PermissionRoute permission={Permission.VIEW_BILLING}>
                    <InvoiceList />
                  </PermissionRoute>
                }
              />
              <Route
                path="invoices/new"
                element={
                  <PermissionRoute permission={Permission.MANAGE_BILLING}>
                    <CreateInvoice />
                  </PermissionRoute>
                }
              />
              <Route
                path="deals"
                element={
                  <PermissionRoute permission={Permission.VIEW_DEALS}>
                    <Deals />
                  </PermissionRoute>
                }
              />
              <Route
                path="usage"
                element={
                  <PermissionRoute permission={Permission.VIEW_COMPANY_USAGE}>
                    <TeamUsage />
                  </PermissionRoute>
                }
              />
              <Route
                path="projects"
                element={
                  <PermissionRoute permission={Permission.VIEW_PROJECTS}>
                    <ProjectsHome />
                  </PermissionRoute>
                }
              />
              <Route
                path="projects/:projectId"
                element={
                  <PermissionRoute permission={Permission.VIEW_PROJECTS}>
                    <ProjectBoard />
                  </PermissionRoute>
                }
              />
              <Route path="profile" element={<ProfileSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
